import React, { ReactNode } from 'react'
import { StyleSheet, TextStyle, TouchableOpacity, TouchableOpacityProps, ViewStyle } from 'react-native'
import { Spacing } from './Spacing'
import { DSTypographyVariant, Text } from './Typography'
import { Frame } from './Frame'
import { DSColor, Palette } from '@ppwr/poker'
import { Dimensions } from 'react-native'

type DSButtonVariant =
  | 'Large/Primary'
  | 'Large/Secondary'
  | 'Medium/Primary'
  | 'Medium/Secondary'
  | 'Small/Primary'
  | 'Small/Secondary'
  | 'Small/Tertiary'

const windowWidth = Dimensions.get('window').width

const variants: Record<DSButtonVariant, ViewStyle> = {
  'Large/Primary': {
    paddingVertical: Spacing['14.5'],
    paddingHorizontal: windowWidth < 380 ? Spacing[12] : Spacing[16],
    gap: Spacing[8],
    borderRadius: Spacing[48],
    flexDirection: 'row',
    alignItems: 'center'
  },
  'Large/Secondary': {
    paddingVertical: Spacing['14.5'],
    paddingHorizontal: windowWidth < 380 ? Spacing[12] : Spacing[16],
    gap: Spacing[8],
    borderRadius: Spacing[48],
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: Palette['Brand/Light Grey'],
    borderWidth: 1
  },
  'Medium/Primary': {
    paddingVertical: Spacing['10.5'],
    paddingHorizontal: Spacing['24'],
    gap: Spacing[8],
    borderRadius: Spacing[32],
    flexDirection: 'row',
    alignItems: 'center'
  },
  'Medium/Secondary': {
    paddingVertical: Spacing['10.5'],
    paddingHorizontal: Spacing['24'],
    gap: Spacing[8],
    borderRadius: Spacing[32],
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: Palette['Brand/Light Grey'],
    borderWidth: 1
  },
  'Small/Primary': {
    paddingTop: 5,
    paddingHorizontal: 10,
    paddingBottom: 7,
    gap: Spacing[4],
    borderRadius: Spacing[48],
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: Palette['Brand/Light Grey'],
    borderWidth: 1
  },
  'Small/Secondary': {
    paddingTop: Spacing['6'],
    paddingBottom: Spacing['8'],
    paddingHorizontal: Spacing['10.5'],
    gap: Spacing[4],
    borderRadius: Spacing[32],
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: Palette['Brand/Light Grey'],
    borderWidth: 1
  },
  'Small/Tertiary': {
    paddingTop: 5,
    paddingHorizontal: 10,
    paddingBottom: 7,
    gap: Spacing[4],
    borderRadius: Spacing[48],
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: Palette['Brand/Light Grey'],
    borderWidth: 1
  }
}

export type DSButtonType = 'On Light' | 'On Dark'

const types: Record<DSButtonType, ViewStyle> = {
  'On Light': {
    backgroundColor: Palette['Global/Black']
  },
  'On Dark': {
    backgroundColor: Palette['Global/White']
  }
}

const textVariants: Record<DSButtonVariant, DSTypographyVariant> = {
  'Large/Primary': 'Body Medium/Medium',
  'Large/Secondary': 'Body Medium/Medium',
  'Medium/Primary': 'Body Small/Medium',
  'Medium/Secondary': 'Body Small/Medium',
  'Small/Primary': 'Body Small/Medium',
  'Small/Secondary': 'Body Small/Medium',
  'Small/Tertiary': 'Body Small/Medium'
}
export const buttonTextVariants = textVariants

const textTypes: Record<DSButtonType, DSColor> = {
  'On Light': 'Global/White',
  'On Dark': 'Global/Black'
}
export const buttonTextTypes = textTypes

export interface ButtonProps extends TouchableOpacityProps {
  children: ReactNode
  variant: DSButtonVariant
  type: DSButtonType
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  rightIconContainerStyle?: ViewStyle
  maxWidth?: boolean
  centerContent?: boolean
  centerText?: boolean
  numberOfLines?: number
  backgroundColor?: DSColor
  borderColor?: DSColor
  textColor?: DSColor
  styleText?: TextStyle
  textAlign?: 'center' | 'auto' | 'left' | 'right' | 'justify'
  textContainerStyle?: ViewStyle
}
export interface ButtonSimpleProps extends TouchableOpacityProps {
  children: ReactNode
  variant: DSButtonVariant
  type: DSButtonType
  containerStyle?: ViewStyle | ViewStyle[]
}

export const ButtonSimple = ({ children, containerStyle, variant, type, ...props }: ButtonSimpleProps) => {
  const buttonStyle = {
    ...types[type],
    ...variants[variant]
  }
  return (
    <TouchableOpacity style={[buttonStyle, containerStyle]} {...props}>
      {children}
    </TouchableOpacity>
  )
}
export const Button: React.FC<ButtonProps> = ({
  leftIcon,
  rightIcon,
  rightIconContainerStyle,
  children,
  style,
  variant,
  type,
  maxWidth,
  centerContent,
  centerText,
  numberOfLines,
  backgroundColor,
  borderColor,
  textColor,
  disabled,
  styleText,
  textAlign = 'center',
  textContainerStyle,
  ...props
}) => {
  const hideLeftIcons = !(leftIcon || centerText)
  const hideRightIcons = !(rightIcon || centerText)
  const buttonStyle = [
    types[type],
    variants[variant],
    {
      flex: maxWidth ? 1 : undefined,
      opacity: disabled ? 0.5 : 1
    },
    backgroundColor ? { backgroundColor: Palette[backgroundColor] } : {},
    borderColor ? { borderColor: Palette[borderColor], borderWidth: 1 } : {},
    style
  ]
  return (
    <TouchableOpacity style={buttonStyle} disabled={disabled} {...props}>
      <Frame style={styles.iconContainer} justifyContent="center" hidden={hideLeftIcons}>
        {leftIcon}
      </Frame>
      {/* <Frame flex={centerContent ? undefined : 1}> */}
      <Frame style={[!centerContent ? { flexGrow: 1, alignSelf: 'center' } : {}, textContainerStyle ?? {}]}>
        <Text
          color={textColor ?? textTypes[type]}
          textAlign={textAlign}
          variant={textVariants[variant]}
          numberOfLines={numberOfLines}
          style={[styleText ? styleText : {}]}
        >
          {children}
        </Text>
      </Frame>
      <Frame style={[styles.iconContainer, rightIconContainerStyle]} justifyContent="center" hidden={hideRightIcons}>
        {rightIcon}
      </Frame>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  iconContainer: {
    width: 20,
    height: 'auto'
  }
})
